import clsx from "clsx";
import { type ReactNode } from "react";
import type { SubscriptionStatus, UserStatus, TariffFamily, TariffTier } from "../types/index.js";

export type BadgeVariant =
  | "active"
  | "inactive"
  | "banned"
  | "black"
  | "white"
  | "basic"
  | "pro"
  | "neutral";

export interface BadgeProps {
  variant?:   BadgeVariant;
  children:   ReactNode;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  // Status
  active:   "bg-[var(--color-success-muted)] text-success   border border-success/25",
  inactive: "bg-[var(--color-border)]        text-muted     border border-border",
  banned:   "bg-[var(--color-danger-muted)]  text-danger    border border-danger/25",
  // Tariff family
  black:    "bg-fg/10                        text-fg        border border-fg/15",
  white:    "bg-fg/5                         text-fg/70     border border-fg/10",
  // Tariff tier
  basic:    "bg-surface                      text-muted     border border-border",
  pro:      "bg-[var(--color-accent-muted)]  text-accent    border border-accent/25",
  // Fallback
  neutral:  "bg-surface                      text-muted     border border-border",
};

export function Badge({ variant = "neutral", children, className }: BadgeProps) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium tabular-nums",
        variantClasses[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Map a subscription/user status to the matching Badge variant. */
export function statusBadgeVariant(
  status: SubscriptionStatus | UserStatus,
): BadgeVariant {
  switch (status) {
    case "active":   return "active";
    case "inactive": return "inactive";
    case "banned":   return "banned";
    default:         return "neutral";
  }
}

/** Map tariff family (+ optional tier) to a Badge variant. */
export function tariffBadgeVariant(
  family: TariffFamily,
  tier?:  TariffTier,
): BadgeVariant {
  if (tier === "pro") return "pro";
  return family === "black" ? "black" : "white";
}
